import { JSX, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import { MAX_QIT_IDS_FROM_PARAMS } from 'src/redux/slices/Interview/types';
import { useSurvey } from 'src/contexts/SurveyContext';
import InterviewShell from './InterviewShell';

/**
 * MyDocumentsInterviewRoute — boots the interview engine for a single-question
 * file-management flow launched from the My Documents page.
 *
 * Handles two entry points (self and on-behalf-of):
 * - /renter/survey/:surveyId/my-documents/answer?qits=<qit_id>
 * - /renter/survey/:surveyId/members/:memberId/my-documents/answer?qits=<qit_id>
 *
 * The ?qits param limits the interview to the selected question only.
 * The back button returns to the My Documents page.
 */
export default function MyDocumentsInterviewRoute(): JSX.Element | null {
  const { surveyId, memberId } = useParams<{
    surveyId: string;
    memberId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const { actingOnMemberId } = useSurvey();
  const dispatch = useDispatch();
  const { callIPE } = useIPE();

  const parsedSurveyId = surveyId ? parseInt(surveyId, 10) : null;

  const qitsParam = searchParams.get('qits');
  const onlyQitIds: number[] = qitsParam
    ? qitsParam
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n))
        .slice(0, MAX_QIT_IDS_FROM_PARAMS)
    : [];

  const backPath = memberId
    ? `/renter/survey/${surveyId}/members/${memberId}/my-documents`
    : `/renter/survey/${surveyId}/my-documents`;

  useEffect(() => {
    if (!parsedSurveyId) {
      return;
    }
    dispatch(
      interviewActions.initInterview({
        surveyId: parsedSurveyId,
        onBehalfOf: actingOnMemberId,
        onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : null,
        backPath,
      })
    );
    callIPE({
      surveyIdOverride: parsedSurveyId,
      onBehalfOfOverride: actingOnMemberId,
      onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : undefined,
    });
    return () => {
      dispatch(interviewActions.resetInterview());
    };
    // parsedSurveyId is the boot trigger. Other deps are stable for this route.
  }, [parsedSurveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!parsedSurveyId) {
    return null;
  }

  return <InterviewShell />;
}
